import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarOff, MapPin, Clock, Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, apiError, isLeader } from "@/lib/api";
import { currentLocale } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { JobDialog, AbsenceDialog, NewButton } from "@/components/RecordDialogs";
import { QuickJobDialog } from "@/components/QuickJobDialog";
import AgendaView from "@/components/AgendaView";
import { holidayName } from "@/lib/holidays";
import AvailabilityPanel from "@/components/AvailabilityPanel";
import WeekSchedule from "@/components/WeekSchedule";
import { Button } from "@/components/ui/button";

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const dot = {
  not_started: "bg-slate-400",
  travel_out: "bg-blue-500",
  on_site: "bg-indigo-500",
  working: "bg-green-500",
  paused: "bg-orange-500",
  work_done: "bg-teal-500",
  travel_home: "bg-blue-500",
  completed: "bg-slate-300",
};

const typeDot = {
  field: "bg-green-500",
  workshop: "bg-yellow-400",
  open: "bg-blue-500",
  installation: "bg-purple-600",
};

const typeBar = {
  field: "border-l-4 border-l-green-500",
  workshop: "border-l-4 border-l-yellow-400",
  open: "border-l-4 border-l-blue-500",
  installation: "border-l-4 border-l-purple-600",
};

const typeLabelKey = {
  field: "jobForm.typeField",
  workshop: "jobForm.typeWorkshop",
  open: "jobForm.typeOpen",
};

const shiftIsoDate = (iso, days) => {
  if (!iso) return iso;
  const [datePart, ...rest] = String(iso).split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const nd = new Date(y, m - 1, d + days);
  return [ymd(nd), ...rest].join("T");
};

const chip = {
  not_started: "border-slate-200 bg-white text-slate-800",
  travel_out: "border-blue-200 bg-blue-50 text-blue-800",
  on_site: "border-indigo-200 bg-indigo-50 text-indigo-800",
  working: "border-green-200 bg-green-50 text-green-800",
  paused: "border-orange-200 bg-orange-50 text-orange-900",
  work_done: "border-teal-200 bg-teal-50 text-teal-800",
  travel_home: "border-blue-200 bg-blue-50 text-blue-800",
  completed: "border-slate-200 bg-slate-50 text-slate-500",
  job_paused: "border-orange-300 bg-orange-50 text-orange-900",
  ready_to_invoice: "border-teal-300 bg-teal-50 text-teal-800",
};

export default function CalendarPage() {
  const { t } = useTranslation();
  const { user, company } = useAuth();
  const navigate = useNavigate();
  const leader = isLeader(user?.role) && !company?.demo_readonly;
  const [cursor, setCursor] = useState(() => new Date());
  const [view, setView] = useState(() => localStorage.getItem("fm.calendarView") || "week");
  const [selected, setSelected] = useState(() => ymd(new Date()));
  const [jobs, setJobs] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [staff, setStaff] = useState([]);
  const [jobOpen, setJobOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [absOpen, setAbsOpen] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [activeAbs, setActiveAbs] = useState(null);
  const [initialDate, setInitialDate] = useState(null);
  const [initialUser, setInitialUser] = useState(null);

  const DAYS = useMemo(() => {
    const monday = new Date(2024, 0, 1);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d.toLocaleDateString(currentLocale(), { weekday: "short" });
    });
  }, [t]);

  const monthName = (m) =>
    new Date(2024, m, 1).toLocaleDateString(currentLocale(), { month: "long" });

  const range = useMemo(() => {
    if (view === "week") {
      const d = new Date(cursor);
      const day = (d.getDay() + 6) % 7;
      const start = new Date(d.getFullYear(), d.getMonth(), d.getDate() - day);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { start, end };
    }
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7;
    const start = new Date(first.getFullYear(), first.getMonth(), 1 - offset);
    const end = new Date(start);
    end.setDate(start.getDate() + 41);
    return { start, end };
  }, [cursor, view]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/calendar", {
        params: { start: ymd(range.start), end: `${ymd(range.end)}T23:59:59` },
      });
      setJobs(data.jobs || []);
      setAbsences(data.absences || []);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/users").then(({ data }) => setStaff(data)).catch(() => {});
  }, []);

  const staffName = (id) => staff.find((s) => s.id === id)?.name || t("common.unknown");

  const dayData = useCallback(
    (key) => ({
      jobs: jobs.filter((j) => {
        const from = j.start?.slice(0, 10);
        if (!from) return false;
        const to = (j.end || j.start).slice(0, 10);
        if ((j.excluded_dates || []).includes(key)) return false;
        return from <= key && key <= to;
      }),
      absences: absences.filter((a) => a.start?.slice(0, 10) <= key && a.end?.slice(0, 10) >= key),
    }),
    [jobs, absences]
  );

  const days = useMemo(() => {
    const count = view === "week" ? 7 : 42;
    return Array.from({ length: count }, (_, i) => {
      const d = new Date(range.start);
      d.setDate(range.start.getDate() + i);
      const key = ymd(d);
      return { date: d, key, holiday: holidayName(key), ...dayData(key) };
    });
  }, [range, view, dayData]);

  const shift = (dir) => {
    const d = new Date(cursor);
    if (view === "week") d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setCursor(d);
  };

  const todayKey = ymd(new Date());
  const selectedDay = dayData(selected);

  const weekNumber = (d) => {
    const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const day = t.getUTCDay() || 7;
    t.setUTCDate(t.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
    return Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
  };

  const openWorkOrder = (job) =>
    navigate(job.job_type === "installation" ? `/nymontering/${job.id}` : `/arbetsorder/${job.id}`);
  const openAbs = (abs) => { setActiveAbs(abs); setAbsOpen(true); };
  const editJob = (job) => { setInitialDate(null); setInitialUser(null); setActiveJob(job); setJobOpen(true); };
  const newJobToday = () => { setInitialDate(null); setInitialUser(null); setActiveJob(null); setJobOpen(true); };
  const newJobOnDate = (key, userId = null) => {
    setInitialDate(key);
    setInitialUser(userId);
    setActiveJob(null);
    setJobOpen(true);
  };

  const [dragJob, setDragJob] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const dragProps = (job) =>
    leader
      ? {
          draggable: true,
          onDragStart: (e) => {
            setDragJob(job);
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", job.id);
          },
          onDragEnd: () => { setDragJob(null); setDragOver(null); },
        }
      : {};

  const dropOnDay = async (key) => {
    setDragOver(null);
    const job = dragJob;
    setDragJob(null);
    if (!job?.start) return;
    const from = job.start.slice(0, 10);
    if (from === key) return;
    const utc = (s) => Date.UTC(...s.split("-").map(Number).map((n, i) => (i === 1 ? n - 1 : n)));
    const delta = Math.round((utc(key) - utc(from)) / 86400000);
    const body = { start: shiftIsoDate(job.start, delta) };
    if (job.end) body.end = shiftIsoDate(job.end, delta);
    try {
      await api.put(`/jobs/${job.id}`, body);
      toast.success(t("cal.moved", { title: job.title, date: key }));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const dropOnStaff = async (userId, name) => {
    const job = dragJob;
    setDragJob(null);
    if (!job) return;
    if ((job.assigned_to || []).length === 1 && job.assigned_to[0] === userId) return;
    try {
      await api.put(`/jobs/${job.id}`, { assigned_to: [userId] });
      toast.success(t("cal.reassigned", { title: job.title, name }));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const dayDropProps = (key) =>
    leader
      ? {
          onDragOver: (e) => { if (dragJob) { e.preventDefault(); setDragOver(key); } },
          onDragLeave: () => setDragOver((k) => (k === key ? null : k)),
          onDrop: (e) => { e.preventDefault(); dropOnDay(key); },
        }
      : {};

  const deleteJob = async (job) => {
    if (!window.confirm(t("cal.deleteConfirm", { title: job.title }))) return;
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success(t("cal.deleted"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="calendar-page">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">{t("cal.title")}</h1>
          <p className="text-sm capitalize text-slate-500" data-testid="calendar-period">
            {view === "week"
              ? `${t("common.week")} ${weekNumber(range.start)} · ${range.start.getDate()} – ${range.end.getDate()} ${monthName(range.end.getMonth())} ${range.end.getFullYear()}`
              : `${monthName(cursor.getMonth())} ${cursor.getFullYear()}`}
          </p>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-slate-200 bg-white">
            {["week", "month"].map((v) => (
              <button
                key={v}
                data-testid={`calendar-view-${v}`}
                onClick={() => { setView(v); localStorage.setItem("fm.calendarView", v); }}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  view === v ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {v === "week" ? t("common.week") : t("common.month")}
              </button>
            ))}
          </div>
          <Button variant="outline" size="icon" data-testid="calendar-prev" onClick={() => shift(-1)} className="h-10 w-10 bg-white">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            data-testid="calendar-today"
            onClick={() => { setCursor(new Date()); setSelected(todayKey); }}
            className="h-10 bg-white"
          >
            {t("common.today")}
          </Button>          <Button variant="outline" size="icon" data-testid="calendar-next" onClick={() => shift(1)} className="h-10 w-10 bg-white">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {leader && <NewButton testid="calendar-new-job" label={t("cal.newJob")} onClick={newJobToday} />}
        {!leader && company?.features?.quick_order && (
          <NewButton
            testid="calendar-manual-new-job"
            label="Ny arbetsorder"
            onClick={() => setQuickOpen(true)}
          />
        )}
        <NewButton testid="calendar-new-absence" label={t("cal.newAbsence")} onClick={() => openAbs(null)} />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid="calendar-legend">
        {["field", "workshop", "open", ...(company?.features?.nymontering ? ["installation"] : [])].map((k) => (
          <span key={k} className="flex items-center gap-1.5 text-xs font-medium text-slate-600" data-testid={`legend-${k}`}>
            <span className={`h-2.5 w-2.5 rounded-full ${typeDot[k]}`} /> {k === "installation" ? "nymontering" : t(typeLabelKey[k])}
          </span>
        ))}
        <span className="flex items-center gap-1.5 text-xs font-medium text-slate-600" data-testid="legend-absence">
          <span className="h-2.5 w-2.5 rounded-full bg-orange-500" /> {t("nav.absences")}
        </span>
        <span className="flex items-center gap-1.5 text-xs font-medium text-slate-600" data-testid="legend-holiday">
          <span className="text-red-600">★</span> Röd dag
        </span>
        {leader && <span className="text-xs text-slate-400">{t("cal.dragHint")}</span>}
      </div>

      {/* Datumremsa (vecka) / rutnät (månad) */}
      {view === "week" ? (
        <div data-testid="calendar-week-strip">
          <div className="mb-1.5 flex items-center gap-2">
            <span
              className="rounded bg-amber-200 px-2 py-1 text-xs font-bold uppercase tracking-[0.05em] text-amber-900"
              data-testid="calendar-week-number"
            >
              {t("cal.weekShort")} {weekNumber(range.start)}            </span>
          </div>
          {leader ? (
            <WeekSchedule
              days={days}
              selected={selected}
              todayKey={todayKey}
              dayNames={DAYS}
              typeBar={typeBar}
              dragProps={dragProps}
              dayDropProps={dayDropProps}
              dragOver={dragOver}
              dragJob={dragJob}
              onSelect={setSelected}
              onOpenJob={openWorkOrder}
              onNewJob={newJobOnDate}
              leader={leader}
              staffName={staffName}
            />
          ) : (
            <AgendaView days={days} todayKey={todayKey} dayNames={DAYS} onOpenJob={openWorkOrder} />
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-amber-100 bg-white" data-testid="calendar-month-grid">
          <div className="grid grid-cols-8 border-b border-amber-100 bg-amber-100/60">
            <div className="px-1 py-2 text-center text-[11px] font-bold uppercase tracking-[0.05em] text-amber-900">V.</div>
            {DAYS.map((d) => (
              <div key={d} className="px-1 py-2 text-center text-[11px] font-medium uppercase tracking-[0.05em] text-slate-600">
                {d}
              </div>
            ))}
          </div>
          <div>
            {Array.from({ length: 6 }, (_, wi) => (
              <div key={wi} className="grid grid-cols-8">
                <div
                  data-testid={`calendar-month-week-${wi}`}
                  className="flex items-center justify-center border-b border-r border-amber-100 bg-amber-50 text-xs font-bold text-amber-900"
                >
                  {weekNumber(days[wi * 7].date)}
                </div>
                {days.slice(wi * 7, wi * 7 + 7).map((d) => {
              const outside = d.date.getMonth() !== cursor.getMonth();
              const isSel = d.key === selected;
              return (
                <div
                  key={d.key}
                  {...dayDropProps(d.key)}
                  data-testid={`calendar-dropzone-${d.key}`}
                  className={`relative min-h-[64px] border-b border-r border-slate-100 ${
                    dragOver === d.key
                      ? "bg-blue-100 ring-2 ring-inset ring-dashed ring-blue-500"
                      : isSel
                      ? "bg-blue-50 ring-2 ring-inset ring-blue-500"
                      : outside
                      ? "bg-slate-50/60"
                      : "bg-white"
                  }`}
                >
                <button
                  data-testid={`calendar-day-${d.key}`}
                  onClick={() => setSelected(d.key)}
                  className="relative w-full p-1.5 text-left transition-colors duration-200 hover:bg-slate-50/70"
                >
                  {d.holiday && (
                    <span
                      title={d.holiday}
                      data-testid={`holiday-star-${d.key}`}
                      className="absolute right-1 top-0.5 text-[11px] leading-none text-red-600"
                    >
                      ★
                    </span>
                  )}
                  <span
                    className={`inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-semibold ${
                      d.key === todayKey ? "bg-blue-600 text-white" : outside ? "text-slate-400" : "text-slate-700"
                    }`}
                  >
                    {d.date.getDate()}
                  </span>
                  <div className="mt-1 flex flex-wrap gap-0.5">
                    {d.absences.length > 0 && <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />}
                  </div>
                  {d.jobs.length > 4 && (
                    <span className="mt-1 block text-[10px] font-medium text-slate-500">
                      {d.jobs.length} {t("cal.ordersCount")}
                    </span>
                  )}
                </button>
                <div className="space-y-0.5 px-1 pb-6">
                  {d.jobs.slice(0, 3).map((j) => (
                    <div
                      key={j.id}
                      {...dragProps(j)}
                      data-testid={`month-job-chip-${d.key}-${j.id}`}
                      onClick={() => openWorkOrder(j)}
                      title={j.title}
                      className={`cursor-pointer truncate rounded-sm bg-slate-50 px-1 py-0.5 text-[10px] font-medium text-slate-700 transition-colors duration-200 hover:bg-slate-100 ${
                        typeBar[j.job_type] || typeBar.field
                      } ${dragJob?.id === j.id ? "opacity-40" : ""}`}
                    >
                      {j.start?.slice(11, 16)} {j.title}
                    </div>
                  ))}
                </div>

                {leader && isSel && (
                  <button
                    data-testid={`calendar-add-job-${d.key}`}
                    onClick={() => newJobOnDate(d.key)}
                    title={t("cal.createHere")}
                    aria-label={t("cal.createHere")}
                    className="absolute bottom-1 right-1 flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-white shadow-md transition-colors duration-200 hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                )}
                </div>
              );
            })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Vald dag – lediga anställda (chef, admin, arbetsledare) */}
      {leader && (
        <div className="mt-4">
          <AvailabilityPanel
            date={selected}
            refreshKey={jobs.length + absences.length}
            onBook={(userId) => newJobOnDate(selected, userId)}
            dragActive={!!dragJob}
            onDropJob={dropOnStaff}
          />
        </div>
      )}

      {/* Vald dag – kortlista (agendavyn för tekniker listar redan hela veckan) */}
      {(leader || view === "month") && (
      <div className="mt-4" data-testid="calendar-day-list">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h2 className="text-xl font-semibold capitalize text-slate-800" data-testid="calendar-selected-day">
            {new Date(`${selected}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: "long", day: "numeric", month: "long" })}
          </h2>
          {leader && (
            <Button
              data-testid="calendar-add-job-selected"
              onClick={() => newJobOnDate(selected)}
              className="ml-auto h-10 bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
            >
              <Plus className="mr-1.5 h-4 w-4" /> {t("cal.createHere")}
            </Button>
          )}
        </div>

        <div className="space-y-2">
          {selectedDay.absences.map((a) => (
            <button
              key={`a-${a.id}`}
              data-testid={`day-absence-${a.id}`}
              onClick={() => openAbs(a)}
              className="flex w-full items-center gap-3 rounded-lg border border-orange-200 bg-orange-50 p-3 text-left transition-colors duration-200 hover:bg-orange-100"
            >
              <CalendarOff className="h-4 w-4 shrink-0 text-orange-600" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-orange-900">{staffName(a.user_id)}</div>
                <div className="text-xs text-orange-700">
                  {t(`absType.${a.type}`)} · {a.start?.slice(0, 10)} – {a.end?.slice(0, 10)} · {t(`absStatus.${a.status}`)}
                </div>
              </div>
              <Pencil className="h-3.5 w-3.5 shrink-0 text-orange-600" />
            </button>
          ))}

          {selectedDay.jobs.length === 0 && selectedDay.absences.length === 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500" data-testid="day-list-empty">
              {t("cal.noJobsThisDay")}            </div>
          )}

          {selectedDay.jobs.map((j) => {
            return (
              <div
                key={`j-${j.id}`}
                data-testid={`day-job-card-${j.id}`}
                {...dragProps(j)}
                className={`rounded-lg border border-amber-100 bg-white p-3 transition-colors duration-200 hover:border-blue-300 ${
                  typeBar[j.job_type] || typeBar.field
                } ${leader ? "cursor-grab active:cursor-grabbing" : ""} ${dragJob?.id === j.id ? "opacity-40" : ""}`}
              >
              <button
                data-testid={`day-job-${j.id}`}
                onClick={() => openWorkOrder(j)}
                className="block w-full text-left"
              >
                <div className="flex items-center gap-2">
                  <span
                    data-testid={`day-job-time-${j.id}`}
                    className="flex shrink-0 items-center gap-1.5 rounded bg-slate-900 px-1.5 py-1 text-xs font-semibold text-white"
                  >
                    <Clock className="h-3 w-3" /> {j.start?.slice(11, 16)}
                    {j.end ? `–${j.end.slice(11, 16)}` : ""}
                  </span>
                  <h3 className="min-w-0 flex-1 truncate text-base font-semibold text-slate-900">{j.title}</h3>
                  {j.end && j.end.slice(0, 10) !== j.start.slice(0, 10) && (
                    <span
                      data-testid={`day-job-span-${j.id}`}
                      className="shrink-0 rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-800"
                    >
                      {j.start.slice(0, 10)} → {j.end.slice(0, 10)}
                    </span>
                  )}
                  <span className={`rounded border px-1.5 py-0.5 text-[11px] font-semibold ${chip[j.workflow_status || "not_started"]}`}>
                    {t(`wf.${j.workflow_status || "not_started"}`)}
                  </span>
                </div>
                <div className="mt-1 space-y-0.5 text-sm text-slate-600">
                  {j.customer_name && <p className="font-medium">{j.customer_name}</p>}
                  {j.address && (
                    <p className="flex items-center gap-1.5 text-slate-500">
                      <MapPin className="h-3.5 w-3.5" /> {j.address}
                    </p>
                  )}
                </div>
              </button>
              {leader && (
                <div className="mt-2 flex gap-2 border-t border-slate-100 pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`day-job-edit-${j.id}`}
                    onClick={() => editJob(j)}
                    className="h-9 bg-white transition-colors duration-200 hover:bg-slate-100"
                  >
                    <Pencil className="mr-1.5 h-3.5 w-3.5" /> {t("common.edit")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`day-job-delete-${j.id}`}
                    onClick={() => deleteJob(j)}
                    className="h-9 border-red-200 bg-white text-red-600 transition-colors duration-200 hover:bg-red-50"
                  >
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> {t("common.delete")}
                  </Button>
                </div>
              )}
              </div>
            );
          })}
        </div>
      </div>
      )}

      <JobDialog open={jobOpen} onOpenChange={setJobOpen} job={activeJob} initialDate={initialDate} initialUser={initialUser} staff={staff} onSaved={load} onManual={company?.features?.quick_order ? () => setQuickOpen(true) : undefined} />
      <QuickJobDialog open={quickOpen} onOpenChange={setQuickOpen} staff={staff} onSaved={load} />
      <AbsenceDialog open={absOpen} onOpenChange={setAbsOpen} absence={activeAbs} staff={staff} onSaved={load} />
    </div>
  );
}
